import { useCallback, useEffect, useState } from 'react';

export type UseElementVisibilityOptions = {
  /** Expand the intersection root so nearby items prefetch. Default `240px`. */
  rootMargin?: string;
  /** After first visible, keep `hasBeenVisible` true and stop observing. Default true. */
  once?: boolean;
};

/**
 * Observes an element with IntersectionObserver for lazy load / prefetch gates.
 */
export function useElementVisibility(options?: UseElementVisibilityOptions): {
  ref: (node: Element | null) => void;
  isVisible: boolean;
  hasBeenVisible: boolean;
} {
  const rootMargin = options?.rootMargin ?? '240px';
  const once = options?.once ?? true;
  const [element, setElement] = useState<Element | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  const ref = useCallback((node: Element | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (element == null) {
      return;
    }

    if (once && hasBeenVisible) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const visible = entry?.isIntersecting ?? false;
        setIsVisible(visible);
        if (visible) {
          setHasBeenVisible(true);
        }
      },
      { rootMargin, threshold: 0 },
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [element, hasBeenVisible, once, rootMargin]);

  return { ref, isVisible, hasBeenVisible };
}
